import _ from 'lodash';
import * as members from '../privates/privateMembers';
import * as postMessage from '../privates/postMessage';
import Link from '../ui/Link';
import platformUtils from '../privates/platformUtils';
import {TYPES, validate, validateByType} from '../privates/validations/validation';
import {generateError, errors} from '../privates/validations/errors';
import monitoring from '../privates/monitoring';
import reporter from '../privates/reporter';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

/**
 * @namespace wix-location
 * @summary The wix-location module contains functionality for getting information
 *  about the URL of the current page and for navigating to other pages.
 * @description
 *  To use the location module, import wixLocation from the wix-location module:
 *
 *    ```javascript
 *    import wixLocation from 'wix-location';
 *    ```
 *
 *  The APIs in `wix-location` can only be used in front-end code and only after
 *  the page is ready.
 * 
 *
 *  The URL is broken into:
 *  + [`baseUrl`](wix-location/baseurl)
 *  + [`prefix`](wix-location/prefix)
 *  + [`path`](wix-location/path)
 *  + [`query`](wix-location/query)
 *
 * #### Premium Sites
 * 
 * For premium sites, the URL of the incoming call has the following format:
 * **`https://www.domain.com/myPrefix/myPath?myQuery=myValue`**
 * 
 * + **baseUrl**: &nbsp;&nbsp;&nbsp;&nbsp;`https://www.domain.com`
 * + **prefix**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`myPrefix`
 * + **path**:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; `myPath`
 * + **query**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`myQuery=myValue`
 * 
 * **Example**: 
 * **`https://domain.com/animals/mammals/elephant?species=african-elephant`**
 * + **baseUrl**: &nbsp;&nbsp;&nbsp;&nbsp;`https://domain.com/`
 * + **prefix**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`animals`
 * + **path**:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; `mammals/elephant`
 * + **query**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`species=african-elephant`
 * 
 * #### Free Sites
 * 
 * For free sites, the URL of the incoming call has the following format:
 * **`https://www.domain.com/mySite/myPrefix/myPath?myQuery=myValue`**
 *
 * + **baseUrl**: &nbsp;&nbsp;&nbsp;&nbsp;`https://www.domain.com/mySite`
 * + **prefix**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`myPrefix`
 * + **path**:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; `myPath`
 * + **query**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`myQuery=myValue`
 * 
 * **Example**: 
 * **`https://user.wix-sites.com/zoo/animals/mammals/elephant?species=african-elephant`**
 * + **baseUrl**: &nbsp;&nbsp;&nbsp;&nbsp;`https://user.wix-sites.com/zoo`
 * + **prefix**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`animals`
 * + **path**:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; `mammals/elephant`
 * + **query**: &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;`species=african-elephant`
 *
 */
class Location {
    constructor(appDefId){
        members._appDefId.set(this, appDefId);
        _.bindAll(this, ['to']);
    }

    /**
     * @member url
     * @label property
     * @summary Gets the full URL of the current page.
     * @description
     *  Premium sites:
     *  ![Premium site URL](images/url_premium_url.png "Premium site URL")
     *
     *  Free sites:
     *  ![Free site URL](images/url_free_url.png "Free site URL")
     *
     *  If the site is not yet published, the function returns null.
     * @type {String}
     * @snippet [Location-url.es6=Get the full URL of the current page]
     * @see [baseUrl](#baseUrl)
     * @memberof wix-location
     * @readonly
     */
    get url() {
        return platformUtils.getRGI().getFullUrl();
    }

    /*
     ******* COMMENTED OUT — SEE https://jira.wixpress.com/browse/WCD-3608  ******
     * @member hash
     * @label property
     * @summary Gets the part of the page's URL after the hash (`#`).
     * @description
     *  Premium sites:
     *  ![Premium site hash](images/url_premium_hash.png "Premium site hash")
     *
     *  Free sites:
     *  ![Free site hash](images/url_free_hash.png "Free site hash")
     * @snippet [Location-hash.es6=Get the hash of the current page's URL]
     * @type {String}
     * @memberof wix-location
     * @readonly
     */
    get hash() {
        const locationInfo = platformUtils.getRGI().getLocation();
        return locationInfo.hash;
    }

    /**
     * @member protocol
     * @label property
     * @summary Gets the protocol of the current page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site protocol](images/url_premium_protocol.png "Premium site protocol")
     *
     *  Free sites:
     *  ![Free site protocol](images/url_free_protocol.png "Free site protocol")
     * @snippet [Location-protocol.es6=Get the protocol of the current page's URL]
     * @type {String}
     * @memberof wix-location
     * @readonly
     */
    get protocol() {
        const locationInfo = platformUtils.getRGI().getLocation();
        return locationInfo.protocol;
    }

    /**
     * @member prefix
     * @memberof wix-location
     * @label property
     * @summary Gets the prefix of a dynamic page's or router page's URL.
     * @description
     *  Only dynamic pages and router pages have a prefix. The value of the
     *  `prefix` property for other page types is always `undefined`.
     *
     *  Premium sites:
     *  ![Premium site prefix](images/url_premium_prefix.png "Premium site prefix")
     *
     *  Free sites:
     *  ![Free site prefix](images/url_free_prefix.png "Free site prefix")
     *
     *  To learn more about dynamic page prefixes, see [About URL Prefixes and Page Grouping of Dynamic Pages](https://support.wix.com/en/article/about-url-prefixes-and-page-grouping-of-dynamic-pages).
     *
     *  To learn more about router page prefixes, see [About Routers](https://support.wix.com/en/article/about-routers#url-prefix).
     * @snippet [Location-prefix.es6=Get the prefix of the current page's URL]
     * @type {String}
     * @memberof wix-location
     * @readonly
     */
    get prefix() {
        const locationInfo = platformUtils.getRGI().getLocation();
        return locationInfo.prefix;
    }

    /**
     * @member path
     * @label property
     * @summary Gets the path of the current page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site path](images/url_premium_path.png "Premium site path")
     *
     *  Free sites:
     *  ![Free site path](images/url_free_path.png "Free site path")
     * @snippet [Location-path.es6=Get the path of the current page's URL]
     * @type {String[]}
     * @memberof wix-location
     * @readonly
     */
    get path() {
        const locationInfo = platformUtils.getRGI().getLocation();
        return locationInfo.path;
    }

    /**
     * @member query
     * @label property
     * @summary Gets an object that represents the query segment of the current page's URL.
     * @description
     *  Premium sites:
     *  ![Premium site query](images/url_premium_query.png "Premium site query")
     *
     *  Free sites:
     *  ![Free site query](images/url_free_query.png "Free site query")
     * @snippet [Location-query.es6=Get the query of the current page's URL]
     * @type {Object}
     * @memberof wix-location
     * @readonly
     */
    get query() {
        const locationInfo = platformUtils.getRGI().getLocation();
        return locationInfo.query;
    }

    /**
     * @class wix-location.QueryParams
     * @summary An object used to manage the query segment of the current page's URL.
     */

    /**
     * @member queryParams
     * @label property
     * @summary Gets an object used to manage the query segment of the current page's URL.
     * @snippet [QueryParams-add.es6=Add query parameters to the URL]
     * @snippet [QueryParams-remove.es6=Remove query parameters from the URL]
     * @type {wix-location.QueryParams}
     * @memberof wix-location
     * @readonly
     */
    get queryParams() {
        return {
            /**
             * @function add
             * @summary Adds query parameters to the current page's URL.
             * @description
             *  Adds one or more query parameters to the current page's URL.
             *  
             *  If a specified key already exists as a query parameter, the
             *  newly specified value will overwrite the key's previous value.
             * 
             *  Calling the `add()` function triggers the [`onChange()`](wix-location.html#onChange)
             *  event handler if it has been registered.
             * > ** Note:** To retrieve the page's current query parameters, use the
             *  [`query`](wix-location.html#query) property.
             * @snippet [QueryParams-add.es6=Add query parameters to the URL]
             * @param {wix-location.QueryObject} toAdd An object containing a key:value pair
             *  for each query parameter to add to the URL, where the object's
             *  keys are the query parameter keys and the object's values
             *  are the corresponding query parameter values.
             * @memberof wix-location.QueryParams
             * @see [remove( )](#remove)
             */ 
            
            /**
             * @typedef {Object} wix-location.QueryObject
             * @summary Set of key-value pairs for query parameters and their values.
             * @property {String} key:value Key value pair in the format `key:value`: 
             *                      
             *  + `key`  Any query parameter name of type `string`. 
             *  + `:`  A colon separating the `key` and the `value`.
             *  + `value`  Any query parameter value of type `string`.
             * @snippet [QueryParams-add.es6=Add query parameters to the URL]
             */


            add: queryParamsToAdd => {
                if (!validateByType({value: queryParamsToAdd, expectedType: TYPES.OBJECT})) {
                    return;
                }
                postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                    command: postMessage.MessageTypes.ADD_QUERY_PARAMS,
                    data: queryParamsToAdd || {},
                    appDefinitionId: members._appDefId.get(this)
                });
            },
            /**
             * @function remove
             * @summary Removes query parameters from the current page's URL.
             * @description
             *  Removes one or more query parameters to the current page's URL.
             *  
             *  If a specified key does not exist as a query parameter, it
             *  is ignored.
             * 
             *  Calling the `remove()` function triggers the [`onChange()`](wix-location.html#onChange)
             *  event handler if it has been registered.
             * > **Note:** To retrieve the page's current query parameters, use the
             *  [`query`](wix-location.html#query) property.
             * @snippet [QueryParams-remove.es6=Remove query parameters from the URL]
             * @param {string[]} toRemove List of keys to remove.
             * @see [add( )](#add)
             * @memberof wix-location.QueryParams
             */
            remove: queryParamsToRemove => {
                if (!validateByType({value: queryParamsToRemove, expectedType: TYPES.ARRAY})) {
                    return;
                }
                postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
                    command: postMessage.MessageTypes.REMOVE_QUERY_PARAMS,
                    data: queryParamsToRemove || [],
                    appDefinitionId: members._appDefId.get(this)
                });
            }
        };
    }

    /**
     * @member baseUrl
     * @label property
     * @summary Gets the base URL of the current page.
     * @description
     *  Premium sites:
     *  ![Premium site baseUrl](images/url_premium_baseUrl.png "Premium site baseUrl")
     *
     *  Free sites:
     *  ![Free site baseUrl](images/url_free_baseUrl.png "Free site baseUrl")
     *
     *  If the site is not yet published, the function returns null.
     * @snippet [Location-baseUrl.es6=Get the base URL of the current page]
     * @type {String}
     * @see [url](#url)
     * @memberof wix-location
     * @readonly
     */
    get baseUrl() {
        const RGI = platformUtils.getRGI();
        return RGI.getNavigationInfo().baseUrl;
    }

    /**
     * @function to
     * @summary Directs the browser to navigate to the specified URL.
     * @description
     *  The `to()` function navigates the browser to another web page.
     *
     *  The following link patterns are supported:
     *
     *  + `/localPageURL`: Another page on your site.
     *  + `/localPageURL#<velo-element-id>`: Another page on your site scrolled to the
     *     element with the specified ID. The element must be an element that supports
     *     the [`scrollTo`]($w.Node.html#scrollTo) function.
     *  + `/localPageURL?queryParam=value`: Another page on your site with query parameters.
     *  + `/`: Your site's home page.
     *  + `http(s)://<url>`: An external web address.
     *  + `wix:document://<location>`: A document stored in the Media Manager.
     *  + `mailto:<address>@<someplace.com>?subject=<subject>`: An email.
     *  + `tel:<phone number>`: A phone number.
     *
     *
     *  To find the local URL of a page on your site in the Editor:
     *
     *  + Regular page: See the **SEO** tab of the **Page Settings** panel.
     *  + Dynamic page: See the **Page Info** tab of the **Page Settings** panel
     *    for the URL structure. The actual URL used for navigation needs to contain
     *    values where the placeholders are.
     *
     *    For example, if the URL structure of your dynamic page looks like:
     *
     *    ![Dynamic Page URL](images/dynamic_url.png "Dynamic Page URL")
     *
     *    and you have an item with the title "Waffles", the local URL to that page
     *    is /Recipes/Waffles.
     *  + Router page: You cannot navigate directly to a specific router page. You
     *    can navigate to a URL with the router's prefix and the router code
     *    decides which page to route to.
     *
     *
     *  The `to()` function attempts to properly encode the URL parameter that
     *  is passed to it. For example, `.../some page` is encoded to
     *  `.../some%20page`. However, some URLs do not have one unambiguous encoding.
     *  In those cases it is up to you to encode the URL to reflect your intentions.
     *  Because of these situations, it is a best practice to always encode URLs
     *  before you pass them to the `to()` function.
     *
     *  Note that Wix URLs do not contain spaces. A page which has spaces in its
     *  name has its spaces replaced with dashes (`-`). Similarly, a dynamic page
     *  whose URL contains the value of a field in your collection with spaces
     *  has its spaces replaced with dashes (`-`).
     * 
     *  > ** Note:** The `to()` function does not work while previewing your site.
     * @param {String} url The URL of the page or website to navigate to.
     * @snippet [Location-to_set_local.es6=Navigate to a local link]
     * @snippet [Location-to_set_scroll.es6=Navigate to a local link and scroll to an element]
     *  This example assumes you have a page named **Long Page** that has an element
     *  with the ID `downUnder`. 
     * @snippet [Location-to_set_weblink.es6=Navigate to an external web link]
     * @snippet [Location-to_set_mailto.es6=Open a new email window]
     * @snippet [Location-to_set_document.es6=Open a document]
     * @snippet [Location-to_set_telephone.es6=Make a phone call]
     * @memberof wix-location
     */
    to (url) {
        let linkData;
        if (!validate({compType: 'Location', functionName: 'to', dataToValidate: {url}})) {
            return this;
        }
        try {
            linkData = new Link(url).getData();
        } catch (e) {
            if(e.message === errors.ERROR_API_BEFORE_PAGE_READY){
                throw new monitoring.MonitoringErrorToIgnore(generateError.error_api_with_name_before_page_ready({apiName: 'wix-location.to'}));
            }
            const message = e.message === 'Unsupported link type' ? `${url} is an unsupported url` : e.message;
            reporter.reportSdkError(message);
            return this;
        }

        if (linkData.hasOwnProperty('pageId')) {
            linkData.pageId = linkData.pageId === '#' ? linkData.pageId : linkData.pageId.replace('#', '');
        }

        if (linkData.hasOwnProperty('target')) {
            delete linkData.target;
        }

        postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
            command: postMessage.MessageTypes.NAVIGATE_TO,
            data: linkData,
            appDefinitionId: members._appDefId.get(this)
        });
    }

    /**
     * @typedef {Object} wix-location.Location
     * @summary An object containing information about a location.
     * @property {String} path Location path.
     * @snippet [Location-onChange.es6=Get the new location path]
     */

    /**
     * @callback wix-location.LocationChangeHandler
     * @summary Handles location change events.
     * @param {wix-location.Location} event The new location.
     */

    /**
     * @function onChange
     * @summary Adds an event handler that runs when an application page's URL changes.
     * @description
     * The event handler set by the `onChange` function runs when the location is changed 
     * but the change doesn't trigger navigation. This situation occurs when navigating between 
     * subitems on a page that is managed by a full-page application.
     * 
     * For example, a store product page is a full-page application. When a product page's path 
     * changes because it is switching between items, no actual navigation is taking place. You 
     * can use the `onChange` event handler to determine when a new product is displayed and 
     * perform any necessary partial updates on the current page.
     * 
     * To determine if a page is managed by a full-page application, use the `wix-site` 
     * [`currentPage`](wix-site.html#currentPage) property or [`getSiteStructure()`](wix-site.html#getSiteStructure)
     * function to retrieve a `StructurePage` object that
     * corresponds to the page. If the object contains an `applicationId` value, then the
     * page is managed by a full-page application.
     * @snippet [Location-onChange.es6=Get the new location path]
     * @param {wix-location.LocationChangeHandler} handler The name of the function or
     *  the function expression to run when the location changes.
     * @memberof wix-location
     */
    onChange(callback) {
        if (!validate({compType: 'Location', functionName: 'onStateChanged', dataToValidate: {callback}})) {
            return;
        }
        platformUtils.getRGI().addOnLocationChangeCallback(callback);
    }
}
setStageUnfriendlyFunctions(Location, 'to');

export default Location;
